import { VFC } from 'react';
import { useHooks } from './useHooks';
import { Typography } from '@material-ui/core';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { SiteProcessDatePicker } from '../../input/ProcessDatePicker';
import { TGanttSearchForm } from '../../../../types/process';
import { useStyles } from './useStyles';
import LoadingComponent from '../../../LoadingComponent';
import { DEFAULT_MESSAGE_NO_DATA } from '../../../../const/Const';
import {
  GANTT_CHART_HTML_ID,
  GANTT_SEARCH_HTML_ID
} from '../../../../const/ProcessConst';
import { SiteProcessGanttChart } from '../../process/ProcessGanttChart';
import { SiteTaskListViewModal } from '../../task/TaskListViewModal';

export type TProps = {
  userId: number;
};

export const CalendarGanttView: VFC<TProps> = props => {
  // 検索フォーム関連
  const methods: UseFormReturn<TGanttSearchForm> = useForm({
    mode: 'onChange'
  });
  const {
    reset,
    clearErrors,
    getValues,
    formState: { errors }
  } = methods;

  const { userId } = props;
  const {
    calendarRef,
    initialHeight,
    initialGanttData,
    searchStartDate,
    searchEndDate,
    handleChangeStartDatePicker,
    handleChangeEndDatePicker,
    isLoading,
    isOpenTaskListModal,
    setIsOpenTaskListModal,
    initialVisibleRange,
    initialResourceAreaColumns,
    initialResourceAreaWidth,
    selectedConstructId,
    selectedProcessId,
    afterUpdate,
    handleEditSiteProcess,
    formValidatePeriod,
    includeChildProcess,
    setIncludeChildProcess,
    handleResourceOrderChange
  } = useHooks({
    userId,
    reset,
    getValues,
    clearErrors
  });

  const classes = useStyles();
  return (
    <>
      {/* 検索フォーム部分 */}
      <div className={classes.searchContainer} id={GANTT_SEARCH_HTML_ID}>
        <div className={classes.searchPeriodWrapper}>
          <FormProvider {...methods}>
            <SiteProcessDatePicker
              name='start_date'
              label='開始日'
              required={true}
              fullWidth={true}
              toDate={searchEndDate}
              onChange={handleChangeStartDatePicker}
              className={classes.dateInput}
              errorMessage={
                errors?.start_date?.type === 'required'
                  ? errors.start_date.message
                  : errors?.end_date?.type === 'required'
                  ? undefined
                  : errors?.start_date?.message || errors?.end_date?.message
              }
              errorMessageStyle={
                errors?.start_date?.type === 'required'
                  ? undefined
                  : { display: 'none' }
              }
              validateFunc={formValidatePeriod}
            />
            <div className={classes.waveDash}>
              <Typography>～</Typography>
            </div>
            <SiteProcessDatePicker
              name='end_date'
              label='終了日'
              required={true}
              fullWidth={true}
              fromDate={searchStartDate}
              onChange={handleChangeEndDatePicker}
              className={classes.dateInput}
              errorMessage={
                errors?.end_date?.type === 'required'
                  ? errors.end_date.message
                  : errors?.start_date?.type === 'required'
                  ? undefined
                  : errors?.start_date?.message || errors?.end_date?.message
              }
              errorMessageStyle={
                errors?.end_date?.type === 'required'
                  ? undefined
                  : { display: 'none' }
              }
              validateFunc={formValidatePeriod}
            />
            {errors?.start_date?.type !== 'required' &&
              errors?.end_date?.type !== 'required' && (
                <div className={classes.helperText}>
                  {errors?.start_date?.message || errors?.end_date?.message}
                </div>
              )}
          </FormProvider>
        </div>
      </div>

      {/* ガントチャート */}
      <div id={GANTT_CHART_HTML_ID}>
        {isLoading ? (
          <LoadingComponent />
        ) : initialGanttData?.resources.length > 0 ? (
          <>
            <SiteProcessGanttChart
              calendarRef={calendarRef}
              eventStartEditable={false}
              eventDurationEditable={false}
              displayDailyReport={false}
              initialHeight={initialHeight}
              initialGanttData={initialGanttData}
              initialVisibleRange={initialVisibleRange}
              initialResourceAreaColumns={initialResourceAreaColumns}
              initialResourceAreaWidth={initialResourceAreaWidth}
              handleEditSiteProcess={handleEditSiteProcess}
              handleResourceOrderChange={handleResourceOrderChange}
            />
          </>
        ) : (
          <>
            <div className={classes.noDataMesage}>
              {DEFAULT_MESSAGE_NO_DATA}
            </div>
          </>
        )}
      </div>
      {/* タスク一覧モーダル */}
      <SiteTaskListViewModal
        isOpen={isOpenTaskListModal}
        setIsOpen={setIsOpenTaskListModal}
        constructId={selectedConstructId}
        siteProcessId={selectedProcessId}
        afterUpdate={afterUpdate}
        viewAlert={true}
        includeChildSiteProcess={includeChildProcess}
        setIncludeChildSiteProcess={setIncludeChildProcess}
      />
    </>
  );
};
